import {ProductModel} from '../features/product-model';

const selectors = {
  productComponent: 'product-component',
  addToCart: '[data-add-to-cart]',
  productImage: '[data-product-image]',
  productJson: '[data-product-json]',
  productPage: '.product__page',
  headerSticky: '[data-header-sticky]',
  productMediaList: '[data-product-media-list]',
  form: '[data-product-form]',
  cartBar: '[data-cart-bar]',
  productSubmitAdd: '.product__submit__add',
  formWrapper: '[data-form-wrapper]',
  productVariants: '[data-product-variants]',
  swapUrl: '[data-swap-url]',
  productNotification: 'product-notification',
  notificationPopupButton: '[data-notification-popup-button]',
  popupComponent: 'popup-component',
  popupOpen: '[data-popup-open]',
  inputId: 'input[name="id"][form]',
};

const classes = {
  added: 'is-added',
  expanded: 'is-expanded',
  loading: 'is-loading',
  visible: 'is-visible',
  sticky: 'is-sticky',
};

const attributes = {
  cartBarEnabled: 'data-cart-bar-enabled',
  cartBarAdd: 'data-add-to-cart-bar',
  cartBarScroll: 'data-cart-bar-scroll',
  stickyEnabled: 'data-sticky-enabled',
  swapTarget: 'data-swap-target',
};

if (!customElements.get('product-component')) {
  customElements.define(
    'product-component',
    class ProductComponent extends HTMLElement {
      abortController = undefined;
      pendingRequestUrl = null;
      pendingSwapTarget = null;
      postProcessHtmlCallbacks = [];

      handleClick = (event) => this.handleChange(event);
      handleHover = (event) => this.preFetchAndCache(event);

      constructor() {
        super();

        this.stickyEnabled = this.getAttribute(attributes.stickyEnabled) === 'true';
        this.formWrapper = this.querySelector(selectors.formWrapper);
        this.productNotification = this.querySelector(selectors.productNotification);
        this.cartBarEnabled = this.hasAttribute(attributes.cartBarEnabled);
        this.cartBar = this.querySelector(selectors.cartBar);
        this.setCartBarHeight = this.setCartBarHeight.bind(this);
        this.scrollToTop = this.scrollToTop.bind(this);
        this.toggleCartBarOnScroll = this.toggleCartBarOnScroll.bind(this);
        this.unlockTimer = 0;
        this.swapElements = this.querySelectorAll(selectors.swapUrl);
        this.sectionId = this.dataset.sectionId;
        this.variantPopup = this.querySelector('.product-variant-popup');

        this.manageBVLoadMoreButtonsTabEvent();
      }

      connectedCallback() {
        // Stop parsing if we don't have the product json script tag when loading
        // section in the Theme Editor
        const productJson = this.querySelector(selectors.productJson);
        if ((productJson && !productJson.innerHTML) || !productJson) {
          return;
        }

        const productJsonHandle = JSON.parse(productJson.innerHTML).handle;
        let recentObj = {};
        if (productJsonHandle) {
          recentObj = {
            handle: productJsonHandle,
          };
        }

        // Record recently viewed products when the product page is loading
        Shopify.Products.recordRecentlyViewed(recentObj);
        if (Shopify.Products && Shopify.Products.recordRecentlyViewed) {
          Shopify.Products.recordRecentlyViewed(recentObj);
        }

        this.form = this.querySelector(selectors.form);

        this.bindNotificationPopupEvents();

        if (this.swapElements.length > 0) {
          this.initializeProductSwapUtility();
          this.addEventListener('theme:variant:change', (event) => this.storeOptionValues(event));

          this.swapElements?.forEach((element) => {
            element.addEventListener('click', this.handleClick);
            element.addEventListener('mouseover', this.handleHover);
          });
        }

        if (this.cartBarEnabled) {
          this.initCartBar();
          this.setCartBarHeight();
          document.addEventListener('theme:scroll', this.toggleCartBarOnScroll);
          document.addEventListener('theme:resize', this.setCartBarHeight);
        }

        if (this.variantPopup) {
          this.initVariantPickerPopup();
        }

        this.listenReviewStarsClick();
        //this.sortReviews();
        //this.listenShowMoreReviewClick();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      storeOptionValues(event) {
        this.selectedOptionValues = '';
        const variant = event.detail.variant;
        const selected = event.detail.selected;

        if (!event || !variant) return;

        if (selected.optionValues?.length) {
          this.selectedOptionValues = selected.optionValues;
        }
      }

      preFetchAndCache(event) {
        const element = event.target.closest(selectors.swapUrl);
        if (!element) return;

        const targetUrl = element.dataset.swapUrl;
        const productUrl = targetUrl || this.dataset.url;

        // Don't prefetch if it's the current product URL
        if (this.dataset.url === productUrl) return;

        const requestUrl = `${productUrl}?section_id=${this.sectionId}`;

        // Skip fetching if already cached
        const cacheKey = `product-component-html-${encodeURIComponent(requestUrl)}`;
        if (sessionStorage.getItem(cacheKey)) return;

        // Prefetch and cache the HTML
        fetch(requestUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const bytes = new TextEncoder().encode(responseText).length;
            const megabytes = bytes / (1024 * 1024);
            // Only cache if less than 4MB
            if (megabytes < 4) {
              sessionStorage.setItem(cacheKey, responseText);
            }
          })
          .catch((error) => console.error('Prefetch error:', error));
      }

      handleChange(event) {
        event.preventDefault();
        if (!this.contains(event.target)) return;

        const element = event.target.closest(selectors.swapUrl);
        const targetUrl = element.dataset.swapUrl;
        const productUrl = targetUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        this.pendingSwapTarget = element.dataset.swapTarget;

        const shouldSwapProduct = this.dataset.url !== productUrl;
        if (!shouldSwapProduct) return;

        this.renderProductComponent({
          // Fetch the new product's HTML with section rendering API
          requestUrl: `${productUrl}?section_id=${this.sectionId}`,
          // Returns a function that will process and swap the HTML after fetch completes
          callback: this.handleSwapProduct(productUrl),
        });
      }

      renderProductComponent({requestUrl, callback}) {
        // Check sessionStorage for cached HTML
        const cacheKey = `product-component-html-${encodeURIComponent(requestUrl)}`;
        const cachedHtml = sessionStorage.getItem(cacheKey);

        if (cachedHtml) {
          // Use cached HTML
          callback(new DOMParser().parseFromString(cachedHtml, 'text/html'));
          // Restore scroll position after DOM update
          this.setFocusAndRestoreScroll();
          return;
        }

        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, {signal: this.abortController.signal})
          .then((response) => response.text())
          .then((responseText) => {
            // Store fetched HTML in sessionStorage
            // Check size before storing
            const bytes = new TextEncoder().encode(responseText).length;
            const megabytes = bytes / (1024 * 1024);
            // Only cache if less than 4MB to be safe across browsers
            if (megabytes < 4) {
              sessionStorage.setItem(cacheKey, responseText);
            } else {
              console.warn(`Response too large (${megabytes.toFixed(2)}MB) to cache in sessionStorage`);
            }

            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // Set focus to last clicked sibling link element
            this.setFocusAndRestoreScroll();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      setFocusAndRestoreScroll() {
        if (!this.pendingSwapTarget) return;
        requestAnimationFrame(() => {
          const swapElement = document.querySelector(`[${attributes.swapTarget}="${this.pendingSwapTarget}"]`);
          swapElement?.focus();
          swapElement.scrollIntoView({behavior: 'instant', block: 'center'});
          this.pendingSwapTarget = null;
        });
      }

      handleSwapProduct(productUrl) {
        return (html) => {
          const variantId = this.getSelectedVariantId(html.querySelector(selectors.productComponent));
          this.updateURL(productUrl, variantId);

          window.theme.htmlUpdate.viewTransition(
            this, // Current product-component element to be replaced
            html.querySelector(selectors.productComponent), // New product-component element with updated content
            this.postProcessHtmlCallbacks // Run any post-processing after swap (focus, init components)
          );
        };
      }

      getSelectedVariantId(productComponent) {
        const selectedVariant = productComponent.querySelector(selectors.inputId)?.value;
        return selectedVariant || null;
      }

      updateURL(url, variantId) {
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      bindNotificationPopupEvents() {
        if (!this.productNotification) return;

        this.notificationPopupButtons = this.querySelectorAll(selectors.notificationPopupButton);
        this.notificationPopup = this.productNotification.closest(selectors.popupComponent);
        this.notificationPopupOpen = this.notificationPopup.querySelector(selectors.popupOpen);

        this.notificationPopupButtons.forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            this.notificationPopupOpen.dispatchEvent(new Event('click'));
          });
        });
      }

      initCartBar() {
        // Submit product form on cart bar button click
        this.cartBarBtns = this.cartBar.querySelectorAll(selectors.productSubmitAdd);

        this.cartBarBtns?.forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();

            if (event.currentTarget.hasAttribute(attributes.cartBarAdd)) {
              if (this.cartBarEnabled) {
                event.currentTarget.classList.add(classes.loading);
                event.currentTarget.setAttribute('disabled', 'disabled');
              }

              this.form.querySelector(selectors.addToCart).dispatchEvent(new Event('click', {bubbles: true}));
            } else if (event.currentTarget.hasAttribute(attributes.cartBarScroll)) {
              this.scrollToTop();
            }
          });

          if (button.hasAttribute(attributes.cartBarAdd)) {
            document.addEventListener('theme:product:add-error', this.scrollToTop);
          }
        });

        this.setCartBarHeight();
      }

      scrollToTop() {
        const productVariants = this.querySelector(selectors.productVariants);
        const scrollTarget = !window.theme.isMobile() ? this : productVariants ? productVariants : this.form;
        const scrollTargetTop = scrollTarget.getBoundingClientRect().top;

        window.theme.scrollTo(!window.theme.isMobile() ? scrollTargetTop : scrollTargetTop - 10);
      }

      toggleCartBarOnScroll() {
        const scrolled = window.scrollY;
        const element = theme.settings.productPageSticky && this.formWrapper ? this.formWrapper : this.form;

        if (element && this.cartBar) {
          const formOffset = element.offsetTop;
          const formHeight = element.offsetHeight;
          const checkPosition = scrolled > formOffset + formHeight;

          this.cartBar.classList.toggle(classes.visible, checkPosition);
        }
      }

      setCartBarHeight() {
        const cartBarHeight = this.cartBar.offsetHeight;

        document.documentElement.style.setProperty('--cart-bar-height', `${cartBarHeight}px`);
      }

      disconnectedCallback() {
        document.removeEventListener('theme:product:add-error', this.scrollToTop);

        if (this.cartBarEnabled) {
          document.removeEventListener('theme:scroll', this.toggleCartBarOnScroll);
          document.removeEventListener('theme:resize', this.setCartBarHeight);
        }
      }

      initVariantPickerPopup() {
        const popupVariantPicker = this.querySelectorAll('.product-variant-popup__list-item');
        popupVariantPicker.forEach((item) => {
          item.addEventListener('click', (event) => {
            const variantId = item.getAttribute('data-variant-popup-id');
            const variantTitle = item.getAttribute('data-variant-title');
            const variantText = item.getAttribute('data-variant-text');
            const variantInput = this.querySelector(`.radio__button [data-variant-id="${variantId}"]`);
            variantInput.click();
            popupVariantPicker.forEach((item) => item.classList.remove('active'));
            item.classList.add('active');
            document.querySelector('.product-variant-popup__button').classList.add('active');
            const productSubsWrap = document?.querySelector('.product__subs__wrap');

            if (productSubsWrap) {
              productSubsWrap.classList.remove('visually-hidden');
            }

            if (variantTitle) document.querySelector('.product-variant-popup__button-title').innerHTML = variantTitle;
            if (variantText) document.querySelector('.product-variant-popup__button-text').innerHTML = variantText;

            setTimeout(() => {
              this.variantPopup.popupClose()
            }, 1000)

          })
        })
      }

      listenReviewStarsClick() {
        if (!document.querySelector('.product-info__reviews')) return;

        document.querySelector('.product-info__reviews').addEventListener('click', (e) => {
          e.preventDefault();
          this.scrollToReviews();
        });
      }

      // listenShowMoreReviewClick() {
      //   document.addEventListener('click', (e) => {
      //     const btn = e.target.closest('.bv-content-btn-pages-load-more');
      //     if (!btn) return;
      //
      //     const prevCount = document.querySelectorAll(
      //       '.bv-content-list.bv-content-list-reviews li.bv-content-item.bv-content-review'
      //     ).length;
      //
      //     const listObserver = new MutationObserver((_, obs) => {
      //       const reviews = document.querySelectorAll(
      //         '.bv-content-list.bv-content-list-reviews li.bv-content-item.bv-content-review'
      //       );
      //
      //       if (reviews.length > prevCount) {
      //         this.sortReviews();
      //         obs.disconnect();
      //       }
      //     });
      //
      //     listObserver.observe(document.body, {
      //       childList: true,
      //       subtree: true
      //     });
      //   });
      // }

      scrollToReviews() {
        const target = document.querySelector('[data-bv-show="reviews"]');

        if (!target) return;

        const targetRect = target.getBoundingClientRect();
        const topOffset = window.innerWidth < 768 ? 200 : 220;

        window.scrollTo({
          top: targetRect.top + window.scrollY - topOffset,
          left: 0,
          behavior: 'smooth',
        });

        // focus first focusable element in the reviews container for accessibility
        target.querySelector('input').focus();
      }

      // sortReviews() {
      //   const trySort = () => {
      //     const reviewList = document.querySelector('.bv-content-list.bv-content-list-reviews');
      //     if (!reviewList) return false;
      //
      //     const reviews = Array.from(
      //       reviewList.querySelectorAll('li.bv-content-item.bv-content-review')
      //     );
      //
      //     if (reviews.length < 2) return false;
      //
      //     const getRating = (li) => {
      //       const span = li.querySelector('span.bv-off-screen');
      //       if (!span) return 0;
      //       const match = span.textContent.match(/(\d+)\s*out of/i);
      //       return match ? Number(match[1]) : 0;
      //     };
      //
      //     const sorted = [...reviews].sort((a, b) => getRating(b) - getRating(a));
      //
      //     if (reviews.every((el, i) => el === sorted[i])) return true;
      //
      //     reviewList.replaceChildren(...sorted);
      //     return true;
      //   };
      //
      //   if (trySort()) return;
      //
      //   const observer = new MutationObserver((_, obs) => {
      //     if (trySort()) {
      //       obs.disconnect();
      //     }
      //   });
      //
      //   observer.observe(document.body, {
      //     childList: true,
      //     subtree: true
      //   });
      // }

      manageBVLoadMoreButtonsTabEvent() {
        // manage tab event on BV buttons because BV widget blocking further tab events
        // (u cannot tab further then BV button)

        // if BV is not loaded yet, or buttons are not in the DOM yet
        if (window.BV && !window.bvButtonsFound && document.querySelector('#BVRRContainer')) {
          window.bvButtonsFound = true;
          setTimeout(() => {
            const loadMoreButtons = document.querySelectorAll('.bv-content-btn-pages-load-more');
            loadMoreButtons.forEach(button => {
              button.addEventListener('keydown', (e) => {
                if (e.key === 'Tab') e.target.blur();
              });
            });
          }, 800);
        } else if (!window.bvButtonsFound) {
          setTimeout(this.manageBVLoadMoreButtonsTabEvent.bind(this), 500);
        }  
      }
    }
  );
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
